import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export type ButtonSize = 'sm' | 'md';
export type ButtonTone = 'default' | 'primary' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize;
  tone?: ButtonTone;
  icon?: ReactNode;
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-7 px-2.5 text-[11.5px]',
  md: 'h-8 px-3 text-xs',
};

const TONE_CLASSES: Record<ButtonTone, string> = {
  default: 'bg-bg border border-border text-text hover:bg-surfaceAlt',
  primary:
    'bg-[var(--fm-accent)] text-white border border-transparent hover:opacity-90',
  danger: 'bg-dangerBg text-danger border border-danger/30 hover:bg-danger/10',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      size = 'md',
      tone = 'default',
      icon,
      children,
      className,
      disabled,
      type,
      ...rest
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        disabled={disabled}
        className={cn(
          'inline-flex items-center justify-center gap-1.5 rounded-md font-semibold whitespace-nowrap',
          'active:scale-[0.97] transition-transform duration-75',
          SIZE_CLASSES[size],
          TONE_CLASSES[tone],
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        {...rest}
      >
        {icon}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
